import onReady from '../utils/onReady';
import * as Events from '../utils/events';
import * as ClassList from '../utils/classList';
import triggerCustomEvent from '../utils/triggerCustomEvent';


/**
 *
 * @param {HTMLElement} container
 * @constructor
 */
class Accordion {

    /**
     * The constructor is fired once the class is instantiated.
     *
     * @param {HTMLElement} container
     */
    constructor(container) {
        // Run initializing code once the DOM is ready.
        onReady(() => this.init(container));
    }


    /**
     * Adds events to DOM elements
     *
     */
    addEvents() {
        Events.addEvent(this.dom.toggleBtn, "click", () => {
			if (this.isTransitioning) {
				return;
			}

            // If this is an accordion that lazy loads documents, we need to get them, before we can toggle content
            if (this.filesNeedsLoading) {
                this.loadFiles();
                this.filesNeedsLoading = false;
            } else {
                this.toggle();
            }
        });

        Events.addEvent(this.dom.container, "reCalcHeight", event => {
            this.reCalcHeight(event);
        });

        // Remove isTransitioning flag, when css transition has ended
        Events.addEvent(this.dom.content, "transitionend", () => {
            this.isTransitioning = false;

			if (this.dom.toggleBtn.getAttribute('aria-expanded') === 'false') {
				this.dom.content.hidden = true;
			}
        });
    }

    addErrorText() {
        let errorText = document.createElement('div');
        errorText.innerHTML = `Denne er tom`;
        this.dom.content.appendChild(errorText);
    }

    addFolder(folder) {
        const getFolderMarkup = (folder) => {
            return (
                `
                    <div class="document-list accordion accordion--reverse accordion--load-files" data-module="accordion" data-url="${folder.DownloadUrl}">
                        <h2 class="accordion__header">
                            <button aria-expanded="false" class="accordion__toggleBtn box__text box__text--no-margin">
                                <span class="accordion__toggle" aria-hidden="true" focusable="false">
                                    <svg aria-hidden="true" focusable="false" fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8.59 16.34l4.58-4.59-4.58-4.59L10 5.75l6 6-6 6z"></path>
                                        <path d="M0-.25h24v24H0z" fill="none"></path>
                                    </svg>
                                </span>
                                <span>${folder.FileName}</span>
                            </button>
                        </h2>
                        <section class="document-list__content accordion__content" hidden></section>
                    </div>
                `
            );
        };

        // Make string into html by wrapping it in a div, then select it from that div, and append to dom
        let folderContent = document.createElement('div');
        folderContent.innerHTML = getFolderMarkup(folder);
        let accordionElement = folderContent.querySelector(".accordion");
        this.dom.content.appendChild(accordionElement);

        // Make it an accordion
        accordionElement = new Accordion(accordionElement);
    }

    addHeader() {
        const getHeaderMarkup = () => {
            return (
                `
                    <div class="document-list__header">
                        <div class="row">
                            <div class="document-list__col-1">
                                <p class="pre-text">
                                    type
                                </p>
                            </div>
                            <div class="document-list__col-2">
                                <p class="pre-text">
                                    Navn
                                </p>
                            </div>
                            <div class="document-list__col-3">
                                <p class="pre-text pre-text--align-right">
                                    Størrelse
                                </p>
                            </div>
                            <div class="document-list__col-4">
                                <p class="pre-text pre-text--align-right">
                                    Ændret
                                </p>
                            </div>
                        </div>
                    </div>
                `
            );
        };

        // Make string into html by wrapping it in a div, then select it from that div, and append to dom
        let headerContent = document.createElement('div');
        headerContent.innerHTML = getHeaderMarkup();
        let headerElement = headerContent.querySelector(".document-list__header");
        this.dom.content.appendChild(headerElement);
    }

    addFile(file) {
        const getFileMarkup = (file) => {
            // Svg icon based on mimetype
            const svg =
                file.MimeType === "application/pdf" ?
                    `
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 47.79 46.4"></title><path d="M22.59,26.86a1.61,1.61,0,0,0-.58-.2,4.35,4.35,0,0,0-.69-.05H19.71v2.83h1.61a4.35,4.35,0,0,0,.69-.05,1.6,1.6,0,0,0,.58-.2,1.12,1.12,0,0,0,.4-.43,1.82,1.82,0,0,0,0-1.45A1.12,1.12,0,0,0,22.59,26.86Z" fill="#6d6e71"/><path d="M32.12,27.58a2,2,0,0,0-.84-.63A3.34,3.34,0,0,0,30,26.73H28.52v5.94h1.85a2.65,2.65,0,0,0,.87-.15A1.91,1.91,0,0,0,32,32a2.42,2.42,0,0,0,.53-.88,3.86,3.86,0,0,0,.2-1.32,5.53,5.53,0,0,0-.14-1.29A2.58,2.58,0,0,0,32.12,27.58Z" fill="#6d6e71"/><path d="M45.69,20.56H14.89a2.1,2.1,0,0,0-2.1,2.1V36.53a2.1,2.1,0,0,0,2.1,2.1h30.8a2.1,2.1,0,0,0,2.1-2.1V22.66A2.1,2.1,0,0,0,45.69,20.56ZM25,29.14a2.62,2.62,0,0,1-.57,1,2.82,2.82,0,0,1-1,.68,3.93,3.93,0,0,1-1.5.26H19.71v3.38H17.64V25h4.25a3.92,3.92,0,0,1,1.5.26,2.81,2.81,0,0,1,1,.68,2.62,2.62,0,0,1,.57,1,3.61,3.61,0,0,1,0,2.24Zm9.55,2.42a4.19,4.19,0,0,1-.8,1.5,3.73,3.73,0,0,1-1.33,1,4.51,4.51,0,0,1-1.88.36H26.45V25h4.07a4.86,4.86,0,0,1,1.7.29,3.7,3.7,0,0,1,1.36.87,4,4,0,0,1,.9,1.45,5.87,5.87,0,0,1,.32,2A6.54,6.54,0,0,1,34.53,31.55Zm8.33-4.82H38.3v2.18h3.95v1.61H38.3v3.89H36.23V25h6.63Z" fill="#6d6e71"/><path d="M32.62,41.79A1.61,1.61,0,0,1,31,43.4H4.61A1.61,1.61,0,0,1,3,41.79V4.61A1.61,1.61,0,0,1,4.61,3H22.26l.18.19V13.42a.71.71,0,0,0,.71.71h9.39l.08.08v5.35h3V13.62a1.5,1.5,0,0,0-.4-1L24,.48A1.5,1.5,0,0,0,22.92,0H4.61A4.61,4.61,0,0,0,0,4.61V41.79A4.61,4.61,0,0,0,4.61,46.4H31a4.61,4.61,0,0,0,4.61-4.61V39.63h-3ZM23.86,4.73l7.37,8H23.86Z" fill="#6d6e71"/></svg>
                    ` :
                    `
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 35.62 46.4"></title><path d="M35.22,12.6,24,.48A1.5,1.5,0,0,0,22.92,0H4.61A4.61,4.61,0,0,0,0,4.61V41.79A4.61,4.61,0,0,0,4.61,46.4H31a4.61,4.61,0,0,0,4.61-4.61V13.62A1.5,1.5,0,0,0,35.22,12.6Zm-4,.11H23.63V4.48Zm1.38,29.08A1.61,1.61,0,0,1,31,43.4H4.61A1.61,1.61,0,0,1,3,41.79V4.61A1.61,1.61,0,0,1,4.61,3h17.6V13.42a.71.71,0,0,0,.71.71h9.62l.08.08Z" fill="#6d6e71"/></svg>
                    `;

            return (
                `
                    <div class="document-list__file">
                        <div class="row">
                            <div class="document-list__col-1">
                                <p class="document-list__icon">
                                    ${svg}
                                </p>
                            </div>
                            <div class="document-list__col-2">
                                <a href="${file.DownloadUrl}" target="_blank" class="document-list__filename">
                                    ${file.FileName}
                                </a>
                            </div>
                            <div class="document-list__col-3">
                                <div class="document-list__size">
                                    ${file.Size}
                                </div>
                            </div>
                            <div class="document-list__col-4">
                                <div class="document-list__date">
                                    ${file.Date}
                                </div>
                            </div>
                        </div>
                    </div>
                `
            );
        };

        // Make string into html by wrapping it in a div, then select it from that div, and append to dom
        let fileContent = document.createElement('div');
        // Add file markup
        fileContent.innerHTML += getFileMarkup(file);
        // Find element
        let fileElement = fileContent.querySelector(".document-list__file");
        // Add to dom
        this.dom.content.appendChild(fileElement);
    }

    addContent(responseArray) {
        if (responseArray.length < 1) {
            this.addErrorText();
        } else {
            responseArray.forEach(element => {
                // Type = 0 is file, Type = 1 is folder
                if (element.Type === 1) {
                    this.addFolder(element);
                } else {
                    // Add file header if no header is made yet
                    if (!this.headerMade) {
                        this.headerMade = true;
                        this.addHeader();
                    }

                    this.addFile(element);
                }
            });
        }

        this.toggle();
    }

    handleResponse(response) {
        let contentType = response.headers.get('content-type');
        if (contentType.includes('application/json')) {
            return response.json();
        } else if (contentType.includes('text/html')) {
            return response.text();
        } else {
            throw new Error(`Sorry, content-type ${contentType} not supported`);
        }
    }

    loadFiles() {
        ClassList.add(this.dom.container, "accordion--loading");

        let headers = new Headers({
            "Accept": "application/json, text/plain, text/html"
        });

        let requestObject = {
            method: 'GET',
            headers: headers,
            credentials: 'include'
        };

        let docInfo = fetch(this.fullUrl, requestObject)
            .then(response => {
                if (response.ok) {
                    return this.handleResponse(response);
                } else {
                    return Promise.reject({
                        status: response.status,
                        statusText: response.statusText,
                        err: response
                    });
                }
            })
            .catch(error => {
                window.console.warn(`Error in formFetchSubmit error type: ${error.status}, error status: ${error.statusText}`);
            });

        docInfo
            .then( response => {
                ClassList.remove(this.dom.container, "accordion--loading");
                this.addContent(response);
            });
    }


    /**
     * Opens or closes accordion
     *
     */
    toggle() {
		let expanded = this.dom.toggleBtn.getAttribute('aria-expanded') === 'true' || false;
		this.dom.toggleBtn.setAttribute('aria-expanded', !expanded);
		this.isTransitioning = true;

		if (expanded) {
			ClassList.remove(this.dom.container, "accordion--active");
			this.dom.content.style.maxHeight = "0px";
		} else {
			ClassList.add(this.dom.container, "accordion--active");
			this.dom.content.hidden = false;
			let maxHeight = this.dom.content.scrollHeight;
			this.dom.content.style.maxHeight = `${maxHeight}px`;
			this.bubble();
		}
    }

    /**
     * If this element is a child of an accordion, trigger a recalculation of the parents height, on opening this
     *
     */
    bubble() {
        if (this.dom.parent !== null) {
            let style = window.getComputedStyle(this.dom.content),
                totalHeight = this.dom.content.scrollHeight + parseInt(style.marginTop) + parseInt(style.marginBottom);

            triggerCustomEvent(this.dom.parent, "reCalcHeight", {height: totalHeight});
        }
    }

    /**
     * Recalculating height, since an element inside this has grown.
     *
     */
    reCalcHeight(event) {
        let maxHeight = this.dom.content.scrollHeight + event.detail.height;
        this.dom.content.style.maxHeight = `${maxHeight}px`;

        // Keep bubbling until there's no nested accordions left
        this.bubble();
    }

    /**
     * Delete accordion eventlisteners
     *
     */
    delete() {
        Events.removeEvent(this.dom.toggleBtn, "click");
        Events.removeEvent(this.dom.container, "reCalcHeight");
        Events.removeEvent(this.dom.content, "transitionend");
    }

    /**
	 * Add attributes to mark the relationship between the accordion header and panel
     *
     */
	addAriaAttributes() {
		// To differentiate between each instance of the accordions, we generate a random string to use as ID's
		const randomId = Math.random().toString(20).substr(2, 6);
		this.dom.toggleBtn.setAttribute("id", `accordion-header-${randomId}`);
		this.dom.content.setAttribute("aria-labelledby", `accordion-header-${randomId}`);
		this.dom.toggleBtn.setAttribute("aria-controls", `accordion-panel-${randomId}`);
		this.dom.content.setAttribute("id", `accordion-panel-${randomId}`);
	}

    /**
     * The actual initialization function, fired once the DOM is ready.
     *
     * @param {HTMLElement} container
     */
    init(container) {

        this.dom = {
            container, // ES6 allows this syntax instead of writing "container: container"
            toggleBtn: container.querySelector(".accordion__toggleBtn"),
            content: container.querySelector(".accordion__content"),
            parent: container.parentElement.closest(".accordion")
        };

        // Put your other DOM-specific initializations here, and get your class moving!

        this.isTransitioning = false;

        this.lazyLoadContent = ClassList.contains(this.dom.container, "accordion--load-files");

        if (this.lazyLoadContent) {
            this.filesNeedsLoading = true;
            this.headerMade = false;
            this.url = this.dom.container.dataset.url;
            this.fullUrl = window.location.protocol + "//" + window.location.host + this.url;
        }

        this.addEvents();
        this.addAriaAttributes();
    }

}


export default Accordion;
